import { motion, AnimatePresence } from 'framer-motion';
import { XCircle, CheckCircle2, Info, X, AlertTriangle } from 'lucide-react';
import { useEffect } from 'react';
import { useHapticFeedback } from '@/hooks/useHapticFeedback';

interface ErrorModalProps {
    open: boolean;
    onClose: () => void;
    title?: string;
    message: string;
    type?: 'error' | 'success' | 'info' | 'warning';
}

export const ErrorModal = ({ open, onClose, title, message, type = 'error' }: ErrorModalProps) => {
    const { notificationOccurred } = useHapticFeedback();

    // Effect for Haptics and Auto-close
    useEffect(() => {
        if (open) {
            if (type === 'success') {
                notificationOccurred('success');
                const timer = setTimeout(onClose, 3000);
                return () => clearTimeout(timer);
            } else if (type === 'error') {
                notificationOccurred('error');
            } else {
                notificationOccurred('warning');
            }
        }
    }, [open, type, onClose]);

    if (!open) return null;

    const getIcon = () => {
        switch (type) {
            case 'success': return <CheckCircle2 className="w-12 h-12 text-green-500 drop-shadow-[0_0_15px_rgba(34,197,94,0.5)]" />;
            case 'info': return <Info className="w-12 h-12 text-blue-500 drop-shadow-[0_0_15px_rgba(59,130,246,0.5)]" />;
            case 'warning': return <AlertTriangle className="w-12 h-12 text-orange-500 drop-shadow-[0_0_15px_rgba(249,115,22,0.5)]" />;
            default: return <XCircle className="w-12 h-12 text-red-500 drop-shadow-[0_0_15px_rgba(239,68,68,0.5)]" />;
        }
    };

    const getButtonColor = () => {
        switch (type) {
            case 'success': return 'bg-gradient-to-r from-green-500 to-emerald-600 shadow-[0_4px_12px_rgba(34,197,94,0.3)]';
            case 'info': return 'bg-gradient-to-r from-blue-500 to-indigo-600 shadow-[0_4px_12px_rgba(59,130,246,0.3)]';
            case 'warning': return 'bg-gradient-to-r from-orange-500 to-amber-600 shadow-[0_4px_12px_rgba(249,115,22,0.3)]';
            default: return 'bg-gradient-to-r from-red-500 to-rose-600 shadow-[0_4px_12px_rgba(239,68,68,0.3)]';
        }
    };

    return (
        <AnimatePresence>
            {open && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center px-6">
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                        className="absolute inset-0 bg-black/70 backdrop-blur-md"
                    />

                    {/* Modal Content */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.9, y: 20 }}
                        transition={{ type: "spring", damping: 25, stiffness: 300 }}
                        className="relative w-full max-w-sm bg-[#1c1c1e] rounded-[32px] overflow-hidden shadow-2xl border border-white/5"
                    >
                        {/* Close Button */}
                        <button
                            onClick={onClose}
                            className="absolute top-4 right-4 p-2 text-white/20 hover:text-white transition-colors"
                        >
                            <X size={20} />
                        </button>

                        <div className="p-8 flex flex-col items-center text-center">
                            <motion.div
                                initial={{ scale: 0, rotate: -20 }}
                                animate={{ scale: 1, rotate: 0 }}
                                transition={{ delay: 0.1, type: "spring" }}
                                className="mb-6 bg-white/5 p-5 rounded-full ring-1 ring-white/10"
                            >
                                {getIcon()}
                            </motion.div>

                            <h3 className="text-xl font-black text-white mb-3 tracking-tight">
                                {title || (type === 'error' ? 'Whoops!' : type === 'success' ? 'Success!' : 'Notice')}
                            </h3>

                            <p className="text-white/70 text-sm leading-relaxed mb-8 font-medium">
                                {message}
                            </p>

                            <button
                                onClick={onClose}
                                className={`w-full py-4 rounded-xl font-bold text-white transition-all active:scale-[0.98] ${getButtonColor()}`}
                            >
                                {type === 'error' ? 'Close' : 'Okay'}
                            </button>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
};
